"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { blockCollabUserAction, setCollabMutedAction } from "@frontend/lib/actions";
import { useRouter } from "@frontend/lib/navigation";
import { cx } from "@frontend/components/ui";

type Props = {
  collabId: string;
  counterpartName: string;
  muted: boolean;
  blocked: boolean;
  onViewProfile?: () => void;
};

/** Compact ··· menu — mute, block, profile without crowding the header. */
export function CollabHeaderMenu({
  collabId,
  counterpartName,
  muted,
  blocked,
  onViewProfile,
}: Props) {
  const router = useRouter();
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [isBlocked, setIsBlocked] = useState(blocked);
  const [pending, setPending] = useState<"mute" | "block" | null>(null);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    setIsMuted(muted);
  }, [muted]);
  useEffect(() => {
    setIsBlocked(blocked);
  }, [blocked]);

  useEffect(() => {
    if (!open) return;

    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setCoords({
        top: rect.bottom + 6,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };
    place();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  async function toggleMute() {
    if (pending) return;
    const next = !isMuted;
    setIsMuted(next);
    setPending("mute");
    const form = new FormData();
    form.set("collabId", collabId);
    form.set("muted", next ? "1" : "0");
    const result = await setCollabMutedAction({}, form);
    setPending(null);
    if (result.error) {
      setIsMuted(!next);
      return;
    }
    setOpen(false);
    router.refresh();
  }

  async function onBlock() {
    if (pending) return;
    if (isBlocked) {
      setOpen(false);
      window.location.href = "/settings/blocked";
      return;
    }
    if (
      !window.confirm(
        `Block ${counterpartName}? Their offers and chats will be hidden. You can unblock them later in Settings.`,
      )
    ) {
      return;
    }
    setPending("block");
    const form = new FormData();
    form.set("collabId", collabId);
    const result = await blockCollabUserAction({}, form);
    setPending(null);
    if (result.error) {
      window.alert(result.error);
      return;
    }
    setIsBlocked(true);
    setOpen(false);
    router.push("/collabs");
    router.refresh();
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label="More options"
        title="More options"
        className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-ink-muted transition hover:bg-canvas hover:text-ink"
        onClick={() => setOpen((v) => !v)}
      >
        <EllipsisIcon />
      </button>

      {open && coords && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={panelRef}
              id={menuId}
              role="menu"
              style={{ top: coords.top, right: coords.right }}
              className="fixed z-[110] w-56 overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-[0_16px_40px_-16px_rgba(0,0,0,0.45)]"
            >
              {onViewProfile ? (
                <MenuItem
                  onClick={() => {
                    setOpen(false);
                    onViewProfile();
                  }}
                >
                  View profile
                </MenuItem>
              ) : null}
              <MenuItem disabled={pending === "mute"} onClick={() => void toggleMute()}>
                {isMuted ? "Unmute notifications" : "Mute notifications"}
              </MenuItem>
              <MenuItem
                disabled={pending === "block"}
                danger={!isBlocked}
                onClick={() => void onBlock()}
              >
                {isBlocked ? "Blocked — manage in Settings" : `Block ${counterpartName}`}
              </MenuItem>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
  danger,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cx(
        "flex w-full px-3.5 py-2.5 text-left text-sm transition hover:bg-canvas disabled:opacity-60",
        danger ? "text-alert-600" : "text-ink",
      )}
    >
      {children}
    </button>
  );
}

function EllipsisIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}
